import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ClassesContext — fetches the current list of registered classes (with colors)
 * from the backend once the user is logged in. Auto-discovered classes from
 * Roboflow inference are picked up here without any code change.
 */
const ClassesContext = createContext({
  classes: [],
  classNames: [],
  classColors: {},
  refresh: () => {},
  loading: true,
});

// Fallback defaults (used before context loads / when unauthenticated)
const FALLBACK = [
  { name: "Kardiomegali", color: "#f43f5e" },
  { name: "Nodul", color: "#a855f7" },
  { name: "Pneumonia", color: "#f97316" },
  { name: "Tb paru", color: "#eab308" },
  { name: "normal", color: "#10b981" },
];

export function ClassesProvider({ children }) {
  const { user } = useAuth();
  const [classes, setClasses] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/classes");
      if (Array.isArray(data?.classes) && data.classes.length > 0) {
        setClasses(data.classes);
      }
    } catch {
      // Keep fallback / previous
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else setLoading(false);
  }, [user, refresh]);

  const value = useMemo(() => {
    const classNames = classes.map((c) => c.name);
    const classColors = Object.fromEntries(classes.map((c) => [c.name, c.color]));
    return { classes, classNames, classColors, refresh, loading };
  }, [classes, refresh, loading]);

  return <ClassesContext.Provider value={value}>{children}</ClassesContext.Provider>;
}

export function useClasses() {
  return useContext(ClassesContext);
}
